// src/components/StudentCustomList.jsx
// 学生一覧の「カスタム」表示【2026-09-17】
//
//   表示は「標準」（これまでの表）と「カスタム」の2種類。
//   カスタムは、出す項目・並び順・行の高さ・表の分け方・スクロールのしかた・並べ替えを
//   「表示の設定」で決められ、決めた形でいつも表示される。
//   設定は人ごとにサーバーへ保存する（useUiSetting、v42）。別のパソコンでも同じ表示になる。
//   学生管理の全員表示と、授業の名簿で共通の設定を使う。
//
//   ページからは item を渡す:
//     { key, studentId, name, rate, placeholder, outside, raw,
//       v: { attended, held, absent, late, early, streak, last, since, sinceLabel, section, rosterName, marks } }
import React, { useEffect, useRef, useState } from 'react';
import { STATUS } from './StudentStatusBadges';
import useUiSetting from '../hooks/useUiSetting';

export function rateColor(rate) {
  if (rate == null) return '#9ca3af';
  if (rate < 60) return '#dc2626';
  if (rate < 80) return '#d97706';
  return '#15803d';
}

// ── 設定 ────────────────────────────────────────────────────────
export const CUSTOM_COLUMNS = [
  { key: 'student_id',  label: '学籍番号', sort: 'student_id' },
  { key: 'name',        label: '氏名', required: true, sort: 'name' },
  { key: 'rate',        label: '出席率', sort: 'rate' },
  { key: 'count',       label: '出席回数' },
  { key: 'absent',      label: '欠席', sort: 'absent' },
  { key: 'late',        label: '遅刻' },
  { key: 'early',       label: '早退', onlyIf: 'early' },
  { key: 'streak',      label: '連続欠席', sort: 'streak' },
  { key: 'roster',      label: '名簿' },
  { key: 'account',     label: 'アプリ' },
  { key: 'section',     label: '所属', onlyIf: 'section', sort: 'section' },
  { key: 'last',        label: '最終出席' },
  { key: 'since',       label: '登録日' },
  { key: 'roster_name', label: '名簿の氏名' },
  { key: 'marks',       label: '回ごとの出欠', onlyIf: 'marks' },
  { key: 'edit',        label: '氏名の修正', onlyIf: 'edit' },
];
const COL_KEYS = CUSTOM_COLUMNS.map(c => c.key);
const DEFAULT_COLS = ['name', 'rate', 'count', 'roster'];

export const DENSITY = {
  loose:   { label: 'ゆったり', pad: '0.5rem 0.6rem', font: '0.95rem' },
  normal:  { label: 'ふつう',   pad: '0.28rem 0.5rem', font: '0.88rem' },
  compact: { label: 'ぎゅっと', pad: '0.08rem 0.45rem', font: '0.8rem' },
};
const LAYOUTS = { auto: '自動', one: '1つの表', two: '左右2つ' };
const SCROLLS = { frame: '枠の中でスクロール', page: 'ページ全体でスクロール' };
// 回ごとの出欠の見せ方と、出す回【2026-09-17 出欠表から移した】
export const MARKS_MODES = { columns: '1回1列（第何回・日付）', compact: '記号をまとめて1列' };
export const MARKS_RANGES = { all: 'すべての回', 5: '直近5回', 10: '直近10回' };

const DEFAULT_ORDER = [...DEFAULT_COLS, ...COL_KEYS.filter(k => !DEFAULT_COLS.includes(k))];

//   order = すべての項目の並び（出していない項目も含む）、cols = 出す項目。
//   表の列は order の順に、cols に入っているものだけ並べる。
//   order が無い（以前の設定）ときは normalize が cols の順 → 残り の順で作る。
export const LIST_DEFAULTS = {
  view: 'standard', cols: DEFAULT_COLS, density: 'normal', layout: 'auto', scroll: 'frame',
  marksMode: 'columns', marksRange: 'all',
  sort: { key: 'student_id', dir: 1 },
};

export function normalizeListSettings(v) {
  const out = { ...LIST_DEFAULTS, ...(v || {}) };
  out.view = out.view === 'custom' ? 'custom' : 'standard';
  let cols = Array.isArray(out.cols) ? out.cols.filter((k, i, a) => COL_KEYS.includes(k) && a.indexOf(k) === i) : DEFAULT_COLS;
  if (!cols.includes('name')) cols = ['name', ...cols];
  // 並びが無い（以前の設定）ときは、出す項目の順 → 残り
  const baseOrder = Array.isArray(out.order) ? out.order : cols;
  const order = baseOrder.filter((k, i, a) => COL_KEYS.includes(k) && a.indexOf(k) === i);
  for (const k of COL_KEYS) if (!order.includes(k)) order.push(k);
  out.order = order;
  out.cols = order.filter(k => cols.includes(k));
  if (!DENSITY[out.density]) out.density = 'normal';
  if (!LAYOUTS[out.layout]) out.layout = 'auto';
  if (!SCROLLS[out.scroll]) out.scroll = 'frame';
  if (!MARKS_MODES[out.marksMode]) out.marksMode = 'columns';
  out.marksRange = String(out.marksRange);
  if (!MARKS_RANGES[out.marksRange]) out.marksRange = 'all';
  const s = out.sort || {};
  out.sort = { key: typeof s.key === 'string' ? s.key : 'student_id', dir: s.dir === -1 ? -1 : 1 };
  return out;
}

// 以前ブラウザに覚えていた形（シンプル・一覧・カードなど）から引き継ぐ
function legacySettings() {
  try {
    const view = localStorage.getItem('studentListView');
    if (!view) return null;
    const cols = JSON.parse(localStorage.getItem('studentSimpleCols') || 'null');
    const density = localStorage.getItem('studentSimpleDensity');
    return {
      view: view === 'standard' ? 'standard' : 'custom',
      ...(Array.isArray(cols) ? { cols } : {}),
      ...(density ? { density } : {}),
    };
  } catch (e) { return null; }
}

/** 学生一覧の表示設定（人ごと）。[settings, update, stored] */
export function useStudentListSettings() {
  return useUiSetting('studentList', LIST_DEFAULTS, normalizeListSettings, legacySettings);
}

// ── 小さな部品 ──────────────────────────────────────────────────
/** 標準 / カスタム の切り替え */
export function ViewSwitch({ value, onChange, options }) {
  const opts = options || [['standard', '標準'], ['custom', 'カスタム']];
  return (
    <div role="group" aria-label="表示の切り替え"
         style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
      {opts.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} aria-pressed={value === k}
                style={{ padding: '0.2rem 0.7rem', fontSize: '0.85rem', border: 'none', cursor: 'pointer',
                         background: value === k ? '#334155' : '#fff',
                         color: value === k ? '#fff' : '#334155' }}>
          {label}
        </button>
      ))}
    </div>
  );
}

const chip = (on) => ({
  padding: '0.1rem 0.55rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.8rem',
  border: `1px solid ${on ? '#334155' : '#d1d5db'}`,
  background: on ? '#334155' : '#fff', color: on ? '#fff' : '#374151',
});

function Chips({ options, value, onChange, name }) {
  return (
    <span role="group" aria-label={name} style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap' }}>
      {Object.entries(options).map(([k, v]) => (
        <button key={k} type="button" style={chip(value === k)} aria-pressed={value === k}
                onClick={() => onChange(k)}>{typeof v === 'string' ? v : v.label}</button>
      ))}
    </span>
  );
}

// ── セル ────────────────────────────────────────────────────────
function Cell({ col, it, td, renderEdit }) {
  const v = it.v || {};
  const held = Number(v.held) || 0;
  const dash = (x) => (x == null || x === '' ? '—' : x);
  const times = (x) => (held ? `${x ?? 0}回` : '—');
  const warn = (on) => (on ? { color: '#dc2626', fontWeight: 700 } : {});
  // 項目はすべて左寄せにそろえる（氏名だけ左で他が右だと読みにくいため）【2026-09-17】
  const right = { ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  switch (col.key) {
    case 'student_id':
      return <td style={{ ...td, fontFamily: 'monospace', color: '#4b5563', whiteSpace: 'nowrap' }}>{it.studentId}</td>;
    case 'name':
      return (
        <td style={{ ...td, fontWeight: 600, maxWidth: '16em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {it.name || '（氏名なし）'}
        </td>
      );
    case 'rate':
      return <td style={right}><b style={{ color: rateColor(it.rate) }}>{it.rate != null ? `${Math.round(it.rate)}%` : '—'}</b></td>;
    case 'count':
      return <td style={{ ...right, color: '#4b5563' }}>{held ? `${v.attended ?? 0}/${held}回` : '—'}</td>;
    case 'absent':
      return <td style={{ ...right, ...warn(Number(v.absent) >= 3) }}>{times(v.absent)}</td>;
    case 'late':
      return <td style={right}>{times(v.late)}</td>;
    case 'early':
      return <td style={right}>{times(v.early)}</td>;
    case 'streak':
      return <td style={{ ...right, ...warn(Number(v.streak) >= 2) }}>{times(v.streak)}</td>;
    case 'roster':
      return (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {it.outside
            ? <span title={STATUS.notOnRoster.title} style={{ color: STATUS.notOnRoster.color, fontWeight: 700 }}>なし</span>
            : <span style={{ color: '#15803d' }}>あり</span>}
        </td>
      );
    case 'account':
      return (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {it.placeholder
            ? <span title={STATUS.noAccount.title} style={{ color: STATUS.noAccount.color, fontWeight: 700 }}>未作成</span>
            : <span style={{ color: '#15803d' }}>登録済み</span>}
        </td>
      );
    case 'section':
      return <td style={{ ...td, whiteSpace: 'nowrap', color: '#1d4ed8' }}>{dash(v.section)}</td>;
    case 'last':
      return <td style={{ ...td, whiteSpace: 'nowrap' }}>{held ? (v.last || 'なし') : '—'}</td>;
    case 'since':
      return <td style={{ ...td, whiteSpace: 'nowrap' }}>{dash(v.since)}</td>;
    case 'roster_name':
      return <td style={{ ...td, whiteSpace: 'nowrap', color: '#6b7280' }}>{v.rosterName && v.rosterName !== it.name ? v.rosterName : ''}</td>;
    case 'marks':
      return (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {(it._marks || v.marks || []).map((m, i) => (
            <span key={i} title={m.label} style={{ display: 'inline-block', width: '1.05em', textAlign: 'center', color: m.color, fontWeight: 700 }}>{m.mark}</span>
          ))}
        </td>
      );
    case 'edit':
      return <td style={{ ...td, whiteSpace: 'nowrap' }}>{renderEdit ? renderEdit(it) : null}</td>;
    default:
      return <td style={td} />;
  }
}

// ── 設定の欄 ────────────────────────────────────────────────────
function SettingsPanel({ settings, update, available, labelOf, stored }) {
  const isAvail = (k) => available.some(c => c.key === k);
  const savedOrdered = settings.order.filter(isAvail);
  // つかんで動かしている間は、手元の並び（preview）で表示する
  const [drag, setDrag] = useState(null);   // { key, to, pointerId }
  const ordered = drag
    ? (() => { const rest = savedOrdered.filter(k => k !== drag.key); rest.splice(drag.to, 0, drag.key); return rest; })()
    : savedOrdered;
  // 画面に出ている項目の並びを、全体の order に書き戻す（出ていない項目の位置はそのまま）
  const commitOrdered = (list) => {
    const slots = settings.order.map(k => (isAvail(k) ? null : k));
    let n = 0;
    const order = slots.map(k => (k === null ? list[n++] : k));
    update({ order, cols: settings.cols });
  };
  const toggle = (key) => {
    const col = CUSTOM_COLUMNS.find(c => c.key === key);
    if (col.required) return;
    const cols = settings.cols.includes(key) ? settings.cols.filter(k => k !== key) : [...settings.cols, key];
    update({ cols });
  };
  // キーボード（↑↓）で動かすとき: この画面に出ている項目どうしで入れ替える
  const move = (key, d) => {
    const i = ordered.indexOf(key);
    const j = i + d;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    const other = ordered[j];
    const order = [...settings.order];
    const a = order.indexOf(key), b = order.indexOf(other);
    [order[a], order[b]] = [order[b], order[a]];
    update({ order, cols: settings.cols });
    setMoved({ key, dir: d, at: Date.now() });
  };
  // ── つかんで動かす（マウスでも指でも）──────────────────────
  //   左の「⋮⋮」をつかんで上下に動かし、離したところに入る。
  const onGrab = (e, key) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    if (e.currentTarget.setPointerCapture && e.pointerId != null) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* 取れなくても動く */ }
    }
    setDrag({ key, to: savedOrdered.indexOf(key), from: savedOrdered.indexOf(key) });
  };
  const onDragMove = (e) => {
    if (!drag || !listRef.current) return;
    const y = e.clientY;
    const rows = [...listRef.current.querySelectorAll('[data-col-row]')]
      .filter(li => li.dataset.colRow !== drag.key);
    let to = 0;
    for (const li of rows) {
      const r = li.getBoundingClientRect();
      if (y > r.top + r.height / 2) to++;
    }
    if (to !== drag.to) setDrag(d => (d ? { ...d, to } : d));
  };
  const onDrop = () => {
    if (!drag) return;
    const list = ordered;
    const key = drag.key;
    setDrag(null);
    if (drag.to !== drag.from) {
      commitOrdered(list);
      setMoved({ key, dir: 0, at: Date.now() });
    }
  };
  const onCancel = () => setDrag(null);
  useEffect(() => {
    if (!drag) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrag(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag]);

  // 動かした行を少しのあいだ色付けし、押したボタンを動いた行に付いていかせる
  //   （続けて押せば、その項目をそのまま上へ／下へ運べる）
  const [moved, setMoved] = useState(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (!moved) return undefined;
    const row = listRef.current && listRef.current.querySelector(`[data-col-row="${moved.key}"]`);
    if (moved.dir !== 0) {
      // キーボードで動かしたときは、つかむ所にフォーカスを残す（続けて↑↓を押せる）
      const h = row && row.querySelector('[data-grab]');
      if (h) h.focus();
    }
    const t = setTimeout(() => setMoved(null), 1200);
    return () => clearTimeout(t);
  }, [moved]);
  return (
    <div data-list-settings
         style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0 0 0.5rem', background: '#fff' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.6rem 1.5rem' }}>
        <div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.1rem' }}>出す項目と並び順</div>
          <div style={{ fontSize: '0.74rem', color: '#6b7280', marginBottom: '0.3rem' }}>
            チェックした項目が、上から順に表の左から並びます。左の三本線をつかんで上下に動かすと、行ごと入れ替わります
          </div>
          <ol ref={listRef} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {ordered.map((key, i) => {
              const col = CUSTOM_COLUMNS.find(c => c.key === key);
              const on = settings.cols.includes(key);
              const hot = moved && moved.key === key;
              const dragging = drag && drag.key === key;
              // つかむ所・チェック・項目名を1つの行（枠）にまとめ、行ごと入れ替わるのが見えるようにする
              return (
                <li key={key} data-col-row={key} data-moved={hot ? '1' : undefined} data-dragging={dragging ? '1' : undefined}
                    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.45rem', maxWidth: 360,
                             padding: '0.2rem 0.3rem 0.2rem 0.2rem', fontSize: '0.88rem',
                             border: `1px solid ${dragging || hot ? '#60a5fa' : on ? '#cbd5e1' : '#e5e7eb'}`, borderRadius: 6,
                             background: dragging || hot ? '#dbeafe' : on ? '#fff' : '#f8fafc',
                             boxShadow: dragging ? '0 4px 12px rgba(37,99,235,0.25)' : 'none',
                             color: on ? '#111827' : '#9ca3af', transition: 'background 0.3s, border-color 0.3s' }}>
                  <span role="button" tabIndex={0} aria-label={`${labelOf(col)}の並びを変える`}
                        title="つかんで上下に動かす（↑↓キーでも動かせます）"
                        data-grab={key}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            move(key, e.key === 'ArrowUp' ? -1 : 1);
                          }
                        }}
                        onPointerDown={(e) => onGrab(e, key)}
                        onPointerMove={onDragMove}
                        onPointerUp={onDrop}
                        onPointerCancel={onCancel}
                        style={{ cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none',
                                 display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                 width: 28, height: 26, borderRadius: 4, color: dragging ? '#2563eb' : '#94a3b8' }}>
                    {/* 三本線（つかむ所） */}
                    <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" style={{ display: 'block' }}>
                      <rect x="0" y="0"  width="16" height="2" rx="1" fill="currentColor" />
                      <rect x="0" y="5"  width="16" height="2" rx="1" fill="currentColor" />
                      <rect x="0" y="10" width="16" height="2" rx="1" fill="currentColor" />
                    </svg>
                  </span>
                  <span style={{ width: '1.4em', textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af',
                                 fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0,
                                  cursor: col.required ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={on} disabled={col.required}
                           aria-label={`${labelOf(col)}を表示`} onChange={() => toggle(key)} />
                    <span style={{ fontWeight: on ? 600 : 400 }}>{labelOf(col)}{col.required ? '（常に表示）' : ''}</span>
                  </label>
                  {key === 'marks' && (
                    <span data-marks-options style={{ flexBasis: '100%', display: 'flex', gap: '0.35rem', flexWrap: 'wrap',
                                                      padding: '0.15rem 0 0.05rem 3.4rem' }}>
                      <select value={settings.marksMode} aria-label="回ごとの出欠の見せ方"
                              onChange={e => update({ marksMode: e.target.value })}
                              style={{ fontSize: '0.8rem', padding: '0.1rem 0.2rem' }}>
                        {Object.entries(MARKS_MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <select value={settings.marksRange} aria-label="表示する回"
                              onChange={e => update({ marksRange: e.target.value })}
                              style={{ fontSize: '0.8rem', padding: '0.1rem 0.2rem' }}>
                        {Object.entries(MARKS_RANGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.2rem' }}>行の高さ</div>
            <Chips name="行の高さ" options={DENSITY} value={settings.density} onChange={(k) => update({ density: k })} />
          </div>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.2rem' }}>表の分け方</div>
            <Chips name="表の分け方" options={LAYOUTS} value={settings.layout} onChange={(k) => update({ layout: k })} />
            <div style={{ fontSize: '0.74rem', color: '#6b7280', marginTop: '0.15rem' }}>
              自動: 人数が多く、横幅に余裕があるときだけ左右2つに分けます
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.2rem' }}>スクロール</div>
            <Chips name="スクロール" options={SCROLLS} value={settings.scroll} onChange={(k) => update({ scroll: k })} />
          </div>
          <div style={{ fontSize: '0.76rem', color: '#6b7280' }}>
            {stored === false
              ? 'この設定はこのブラウザにだけ覚えます（サーバーに保存できませんでした）'
              : 'この設定はあなたのアカウントに保存され、次からもこの形で表示されます'}
          </div>
          <div>
            <button type="button" className="btn-link" style={{ fontSize: '0.8rem', padding: 0 }}
                    onClick={() => update({ cols: DEFAULT_COLS, order: DEFAULT_ORDER, density: 'normal', layout: 'auto',
                                            scroll: 'frame', sort: LIST_DEFAULTS.sort })}>
              はじめの設定に戻す
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 本体 ────────────────────────────────────────────────────────
/**
 * @param {object} props
 * @param {Array}  props.items
 * @param {object} props.settings  useStudentListSettings() の値
 * @param {function} props.update  useStudentListSettings() の変更
 * @param {boolean|null} [props.stored]
 * @param {Array<string>} [props.sortKeys] 見出しを押して並べ替えられる項目（sort の名前）
 * @param {function} [props.renderEdit] 「氏名の修正」列の中身 (item) => ReactNode
 * @param {string} [props.sinceLabel] 登録日の見出し（授業の名簿では「履修登録日」）
 */
export default function StudentCustomList({ items: rawItems, settings, update, stored, sortKeys = [], renderEdit,
                                            sinceLabel = '登録日', sessions = [], legend = [] }) {
  const [open, setOpen] = useState(false);
  // 回ごとの出欠: 出す回（直近N回）だけに絞る。v.marks は sessions と同じ順
  const n = settings.marksRange === 'all' ? sessions.length : Math.min(Number(settings.marksRange), sessions.length);
  const from = Math.max(0, sessions.length - n);
  const shownSessions = sessions.slice(from);
  const items = rawItems.map(it => (it.v && Array.isArray(it.v.marks) ? { ...it, _marks: it.v.marks.slice(from) } : it));
  const has = {
    section: items.some(it => it.v && it.v.section),
    marks: items.some(it => it.v && Array.isArray(it.v.marks)),
    early: items.some(it => it.v && it.v.early != null),
    edit: !!renderEdit,
  };
  const available = CUSTOM_COLUMNS.filter(c => !c.onlyIf || has[c.onlyIf]);
  const labelOf = (c) => (c.key === 'since' ? sinceLabel : c.label);
  const shown = settings.order.filter(k => settings.cols.includes(k))
    .map(k => available.find(c => c.key === k)).filter(Boolean);

  const d = DENSITY[settings.density];
  const th = { position: settings.scroll === 'frame' ? 'sticky' : 'static', top: 0, background: '#f1f5f9', zIndex: 1,
               padding: '0.3rem 0.5rem', fontSize: '0.78rem', color: '#475569', fontWeight: 600,
               borderBottom: '1px solid #cbd5e1', whiteSpace: 'nowrap', userSelect: 'none' };
  const td = { padding: d.pad, borderBottom: '1px solid #eef2f7', fontSize: d.font };

  const marksShown = shown.some(c => c.key === 'marks');
  const marksCols = marksShown && settings.marksMode === 'columns';
  const tableMin = 180 + shown.length * 75
    + (marksShown ? (marksCols ? shownSessions.length * 40 : shownSessions.length * 16) : 0);
  // 回ごとの出欠を1回1列で出すときは横に長くなるので、自動なら1つの表にする
  const split = settings.layout === 'two' || (settings.layout === 'auto' && items.length > 12 && !marksCols);
  const half = Math.ceil(items.length / 2);
  const groups = split && items.length > 1 ? [items.slice(0, half), items.slice(half)] : [items];
  const sort = settings.sort;
  const onHead = (c) => {
    if (!c.sort || !sortKeys.includes(c.sort)) return;
    update({ sort: sort.key === c.sort ? { key: c.sort, dir: -sort.dir } : { key: c.sort, dir: c.sort === 'streak' || c.sort === 'absent' ? -1 : 1 } });
  };

  return (
    <div data-custom-list>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', margin: '0 0 0.35rem' }}>
        <button type="button" className="btn-secondary" aria-expanded={open}
                style={{ padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}
                onClick={() => setOpen(o => !o)}>
          表示の設定 {open ? '▲' : '▼'}
        </button>
        <span style={{ fontSize: '0.74rem', color: '#6b7280' }}>
          {shown.map(labelOf).join('・')}／{d.label}／{LAYOUTS[settings.layout]}／{SCROLLS[settings.scroll]}
        </span>
      </div>
      {open && <SettingsPanel settings={settings} update={update} available={available} labelOf={labelOf} stored={stored} />}
      {marksShown && legend.length > 0 && (
        <div data-marks-legend style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 0.35rem' }}>
          {legend.map(m => (
            <span key={m.label} style={{ marginRight: '0.7rem' }}>
              <b style={{ color: m.color }}>{m.mark}</b> {m.label}
            </span>
          ))}
          {sessions.length > shownSessions.length && <span>（{MARKS_RANGES[settings.marksRange]}を表示中・全{sessions.length}回）</span>}
        </div>
      )}

      <div role="list" data-view="custom" data-density={settings.density} data-layout={split ? 'two' : 'one'}
           style={{ display: 'flex', flexWrap: settings.layout === 'two' ? 'nowrap' : 'wrap', gap: '0 1.2rem',
                    alignItems: 'flex-start', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
                    overflowX: 'auto',
                    ...(settings.scroll === 'frame' ? { maxHeight: 'min(75vh, 900px)', overflowY: 'auto' } : {}) }}>
        {groups.map((group, gi) => (
          <table key={gi} data-table-index={gi}
                 style={{ flex: `1 1 ${tableMin}px`, minWidth: settings.layout === 'two' ? tableMin : 0, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {shown.map(c => {
                  if (c.key === 'marks' && marksCols) {
                    if (shownSessions.length === 0) {
                      return <th key="marks" style={th}>回ごとの出欠</th>;
                    }
                    return shownSessions.map(s => (
                      <th key={'s' + s.id} data-session={s.id} title={String(s.date).slice(0, 10)}
                          style={{ ...th, textAlign: 'left', padding: '0.25rem 0.35rem' }}>
                        第{s.no}回
                        <div style={{ fontSize: '0.68rem', fontWeight: 400, color: '#64748b' }}>{String(s.date).slice(5, 10)}</div>
                      </th>
                    ));
                  }
                  const sortable = c.sort && sortKeys.includes(c.sort);
                  return (
                    <th key={c.key} onClick={() => onHead(c)}
                        title={sortable ? 'クリックで並べ替え' : undefined}
                        style={{ ...th, textAlign: c.align || 'left', cursor: sortable ? 'pointer' : 'default' }}>
                      {labelOf(c)}{sortable && sort.key === c.sort ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {group.map(it => (
                <tr key={it.key} role="listitem" data-student={it.studentId}
                    title={[it.studentId, it.name, it.placeholder ? STATUS.noAccount.label : '', it.outside ? STATUS.notOnRoster.label : '']
                      .filter(Boolean).join('\n')}>
                  {shown.map(c => {
                    if (c.key === 'marks' && marksCols) {
                      if (shownSessions.length === 0) return <td key="marks" style={{ ...td, color: '#9ca3af' }}>まだ実施なし</td>;
                      return (it._marks || []).map((m, i) => (
                        <td key={'s' + i} title={m.label}
                            style={{ ...td, padding: d.pad.replace(/\S+$/, '0.35rem'), color: m.color, fontWeight: 700 }}>
                          {m.mark}
                        </td>
                      ));
                    }
                    return <Cell key={c.key} col={c} it={it} td={td} renderEdit={renderEdit} />;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}
